import {
  Box,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as R from 'ramda';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  FeedbackRatings,
  FeedbackSuccess,
  ModalContentContainer,
} from '@/components';
import { AnalyticsService } from '@/services';
import {
  playerSessionsSelectors,
  selectActiveModalParams,
  selectUserCompliance,
  uiActions,
  useAppSelector,
  useFetchEpisodeQuery,
  useSubmitEpisodeEvaluationMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export const EpisodeEvaluationModalContent = () => {
  const dispatch = useDispatch();
  const { episodeId } = useAppSelector(selectActiveModalParams) || {};
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId),
  );
  const compliance = useAppSelector(selectUserCompliance);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});

  const { data: episode } = useFetchEpisodeQuery(
    { episodeId, compliance },
    { skip: !episodeId },
  );

  const [submitEvaluationRequest, { isLoading: isSubmitting }] =
    useSubmitEpisodeEvaluationMutation();

  const questions = [
    {
      id: 'q1',
      text: 'Stated learning objectives were met and stated prerequisite requirements were appropriate and sufficient.',
      value: answers.q1,
      updateValue: (value: number) => setAnswers({ ...answers, q1: value }),
    },
    {
      id: 'q2',
      text: 'Program materials, including the qualified assessment, if any, were relevant and contributed to the achievement of the learning objectives.',
      value: answers.q2,
      updateValue: (value: number) => setAnswers({ ...answers, q2: value }),
    },
    {
      id: 'q3',
      text: 'Time allotted to the learning activity was appropriate.',
      value: answers.q3,
      updateValue: (value: number) => setAnswers({ ...answers, q3: value }),
    },
  ];

  const isFormReady = questions.every(q => !R.isNil(q.value));

  const submitFeedback = async () => {
    if (!playerSession) return;
    try {
      const request = submitEvaluationRequest({
        sessionId: playerSession.sessionId,
        response: questions.map(q => ({
          id: q.id,
          question: q.text,
          answer: q.value,
          type: 'number',
        })),
      });

      await request.unwrap();
      AnalyticsService.episodeEvaluation({ episodeId, answers });

      setIsFeedbackSubmitted(true);
    } catch {
      SnackbarUtils.error(
        'Something went wrong. Please try again or contact support.',
      );
    }
  };

  const onClose = () => {
    dispatch(uiActions.closeActiveModal());
  };

  return (
    <DialogContent sx={[!isMobile && { width: 800, height: 800 }, { p: 0 }]}>
      <ModalContentContainer
        onModalClose={onClose}
        stretch
        leftColumnContents={
          <Box sx={{ maxWidth: 200, mx: 'auto' }}>
            {!!episode && (
              <>
                <Box
                  component="img"
                  alt="episode cover"
                  sx={[{ width: 200, height: 200 }, styles.coverImage]}
                  src={episode.cover}
                />
                <Typography variant="h4" sx={{ color: 'text.contrast' }}>
                  {episode.name}
                </Typography>
              </>
            )}
          </Box>
        }>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
          {isFeedbackSubmitted ? (
            <>
              <FeedbackSuccess />
              <Button
                variant="primary"
                label="Done"
                skinny={false}
                onClick={onClose}
                sx={{ mt: 'auto' }}
              />
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                flex: 1,
              }}>
              <Typography variant="h2" mb={1}>
                Learning Feedback
              </Typography>
              {isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                  <Box
                    component="img"
                    alt="episode cover"
                    sx={[{ width: 100, height: 100 }, styles.coverImage]}
                    src={episode?.cover}
                  />
                  <Typography
                    variant="body3SemiBold"
                    sx={{
                      maxWidth: 300,
                      textAlign: 'center',
                    }}>
                    {episode?.name}
                  </Typography>
                </Box>
              )}
              {questions.map(({ id, text, value, updateValue }) => (
                <Box key={id}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {text}
                  </Typography>
                  <FeedbackRatings
                    rating={value}
                    onRatingChange={updateValue}
                    ratingScale={5}
                  />
                </Box>
              ))}
              <Button
                variant="primary"
                label="Submit"
                skinny={false}
                onClick={submitFeedback}
                isLoading={isSubmitting}
                disabled={!isFormReady}
                sx={{ mt: 'auto' }}
              />
            </Box>
          )}
        </Box>
      </ModalContentContainer>
    </DialogContent>
  );
};

const styles = {
  coverImage: {
    borderRadius: 4,
    overflow: 'hidden',
    boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.65)',
    objectFit: 'cover',
    objectPosition: 'center',
    mb: 1.5,
  },
};
